import React, { useEffect, useState, Children } from 'react';
import { BookOpenIcon, UsersIcon, SettingsIcon, BellIcon, DownloadIcon, PlusIcon, FileTextIcon, ChevronRightIcon, ChevronLeftIcon, MessageCircleIcon, InfoIcon, AlertCircleIcon, BrainIcon, HeartIcon, StarIcon, Loader2Icon } from 'lucide-react';
import { CreateChildAccount } from './CreateChildAccount';
import { useNavigate } from 'react-router-dom';
type TabType = 'stories' | 'children' | 'settings' | 'notifications';
type ParentDashboardProps = {
  onBack: () => void;
  onChildSelect: (child: {
    id: string;
    name: string;
    avatar: string;
    age: number;
    readingLevel: 'beginner' | 'intermediate' | 'advanced';
  }) => void;
};
export const ParentDashboard = ({
  onBack,
  onChildSelect
}: ParentDashboardProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('stories');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const tabs: {
    id: TabType;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    id: 'stories',
    label: 'Story Archive',
    icon: <BookOpenIcon size={20} data-id="element-82" />
  }, {
    id: 'children',
    label: 'Child Accounts',
    icon: <UsersIcon size={20} data-id="element-83" />
  }, {
    id: 'settings',
    label: 'Story Rules',
    icon: <SettingsIcon size={20} data-id="element-84" />
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: <BellIcon size={20} data-id="element-85" />
  }];
  const renderContent = () => {
    switch (activeTab) {
      case 'stories':
        return <StoryArchive data-id="element-86" />;
      case 'children':
        return <ChildAccounts data-id="element-87" />;
      case 'settings':
        return <StoryRules data-id="element-88" />;
      case 'notifications':
        return <div className="space-y-6" data-id="element-89">
            <h2 className="text-xl font-bold text-[#6B4B3E]" data-id="element-90">
              Notification Settings
            </h2>
            <div className="bg-white p-4 rounded-xl shadow-sm" data-id="element-91">
              <label className="flex items-center justify-between" data-id="element-92">
                <span className="text-[#6B4B3E]" data-id="element-93">
                  Email me when a new story is created
                </span>
                <input type="checkbox" checked={emailNotifications} onChange={e => setEmailNotifications(e.target.checked)} className="form-checkbox h-5 w-5 text-[#D4A088] rounded border-[#E5C1A9]" data-id="element-94" />
              </label>
            </div>
          </div>;
    }
  };
  const handleChildSelect = (childId: string) => {
    const selectedChildData = children.find(child => child.id === childId);
    if (selectedChildData) {
      onChildSelect(selectedChildData);
    }
    setSelectedChild(childId);
  };
  return <div className="min-h-screen bg-[#FFFBF6] p-6" data-id="element-95">
      <div className="max-w-6xl mx-auto" data-id="element-96">
        <div className="flex items-center justify-between mb-8" data-id="element-97">
          <div className="flex items-center" data-id="element-98">
            <div onClick={() => navigate('/')} className="mr-4 p-2 hover:bg-[#E5C1A9]/20 rounded-full transition-colors cursor-pointer" data-id="element-99">
              <ChevronLeftIcon size={24} className="text-[#6B4B3E]" data-id="element-100" />
            </div>
            <h1 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-101">
              Parent Dashboard (Beta)
            </h1>
          </div>
          <div className="flex items-center space-x-4" data-id="element-102">
            {selectedChild && <button onClick={() => setSelectedChild(null)} className="flex items-center space-x-2 px-4 py-2 bg-[#E5C1A9]/20 text-[#6B4B3E] rounded-full hover:bg-[#E5C1A9]/30 transition-colors" data-id="element-103">
                <span data-id="element-104">Back to Dashboard</span>
              </button>}
            <button onClick={() => {}} className="text-[#6B4B3E] hover:text-[#D4A088]" data-id="element-105">
              Logout
            </button>
          </div>
        </div>
        {selectedChild ? <ChildAccountDetails childId={selectedChild} onBack={() => setSelectedChild(null)} data-id="element-106" /> : <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-107">
            {/* Sidebar */}
            <div className="space-y-2" data-id="element-108">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === tab.id ? 'bg-white shadow-sm text-[#6B4B3E]' : 'text-[#6B4B3E]/75 hover:bg-white hover:shadow-sm'}`} data-id="element-109">
                  {tab.icon}
                  <span data-id="element-110">{tab.label}</span>
                </button>)}
            </div>
            {/* Main Content */}
            <div className="md:col-span-3 bg-[#FFFBF6] rounded-xl" data-id="element-111">
              {renderContent()}
            </div>
          </div>}
      </div>
    </div>;
};
const ChildAccountDetails = ({
  childId,
  onBack
}: {
  childId: string;
  onBack: () => void;
}) => {
  return <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-112">
      <div className="flex items-center justify-between mb-6" data-id="element-113">
        <div className="flex items-center space-x-4" data-id="element-114">
          <div className="w-16 h-16 bg-[#FFF0E6] rounded-full flex items-center justify-center text-3xl" data-id="element-115">
            🦄
          </div>
          <div data-id="element-116">
            <h2 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-117">
              Emma's Account
            </h2>
            <div className="flex items-center space-x-2 mt-1" data-id="element-118">
              <span className="text-sm bg-[#FFF0E6] text-[#6B4B3E] px-2 py-1 rounded-full" data-id="element-119">
                Age 7
              </span>
              <span className="text-sm bg-[#FFF0E6] text-[#6B4B3E] px-2 py-1 rounded-full capitalize" data-id="element-120">
                Intermediate Reader
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-121">
        <div className="space-y-4" data-id="element-122">
          <h3 className="font-medium text-[#6B4B3E]" data-id="element-123">Reading Progress</h3>
          <div className="bg-[#FFFBF6] rounded-xl p-4" data-id="element-124">
            <div className="flex items-center justify-between mb-2" data-id="element-125">
              <span className="text-sm text-[#6B4B3E]" data-id="element-126">Stories Created</span>
              <span className="font-bold text-[#6B4B3E]" data-id="element-127">12</span>
            </div>
            <div className="flex items-center justify-between mb-2" data-id="element-128">
              <span className="text-sm text-[#6B4B3E]" data-id="element-129">Reading Time</span>
              <span className="font-bold text-[#6B4B3E]" data-id="element-130">3.5 hours</span>
            </div>
            <div className="flex items-center justify-between" data-id="element-131">
              <span className="text-sm text-[#6B4B3E]" data-id="element-132">Favorite Theme</span>
              <span className="font-bold text-[#6B4B3E]" data-id="element-133">Adventure</span>
            </div>
          </div>
        </div>
        <div className="space-y-4" data-id="element-134">
          <h3 className="font-medium text-[#6B4B3E]" data-id="element-135">Recent Activity</h3>
          <div className="bg-[#FFFBF6] rounded-xl p-4 space-y-3" data-id="element-136">
            <div className="flex items-center space-x-3" data-id="element-137">
              <BookOpenIcon size={16} className="text-[#D4A088]" data-id="element-138" />
              <span className="text-sm text-[#6B4B3E]" data-id="element-139">
                Created "The Brave Bunny's Adventure"
              </span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-140">
              <StarIcon size={16} className="text-[#D4A088]" data-id="element-141" />
              <span className="text-sm text-[#6B4B3E]" data-id="element-142">
                Reached reading milestone
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const StoryArchive = () => {
  const [savedBooks, setSavedBooks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStory, setSelectedStory] = useState<string | null>(null);
  useEffect(() => {
    try {
      const storedBooks = JSON.parse(localStorage.getItem('storyBookIds') || '[]');
      setSavedBooks(storedBooks);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to load saved books:', error);
      console.log('Detailed error:', {
        error: errorMessage,
        localStorage: {
          available: typeof localStorage !== 'undefined',
          storyBookIds: localStorage.getItem('storyBookIds')
        }
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);
  return <div className="space-y-6" data-id="element-143">
      <h2 className="text-xl font-bold text-[#6B4B3E]" data-id="element-144">Story Archive</h2>
      <div className="space-y-4" data-id="element-145">
        {isLoading ? <div className="flex items-center justify-center h-32" data-id="element-146">
            <Loader2Icon className="w-8 h-8 animate-spin text-[#D4A088]" data-id="element-147" />
          </div> : error ? <div className="text-center py-12" data-id="element-148">
            <AlertCircleIcon size={48} className="text-red-500 mx-auto mb-4" data-id="element-149" />
            <h3 className="text-xl font-medium text-[#6B4B3E] mb-2" data-id="element-150">
              Error Loading Stories
            </h3>
            <p className="text-[#6B4B3E]/75 mb-2" data-id="element-151">
              There was a problem loading your stories
            </p>
            <p className="text-sm text-red-500 bg-red-50 p-4 rounded-xl max-w-lg mx-auto" data-id="element-152">
              {error}
            </p>
          </div> : savedBooks.length === 0 ? <div className="text-center py-12" data-id="element-153">
            <BookOpenIcon size={48} className="text-[#D4A088] mx-auto mb-4" data-id="element-154" />
            <h3 className="text-xl font-medium text-[#6B4B3E] mb-2" data-id="element-155">
              No stories yet
            </h3>
            <p className="text-[#6B4B3E]/75" data-id="element-156">
              Your child's stories will appear here once they create them
            </p>
          </div> : savedBooks.map(story => <div key={story.id} className="bg-white p-6 rounded-xl shadow-sm" data-id="element-157">
              <h3 className="font-bold text-[#6B4B3E] text-xl mb-4" data-id="element-158">
                {story.summary.name}'s {story.summary.mood} Adventure
              </h3>
              <p className="text-sm text-[#6B4B3E]/75 mb-2" data-id="element-159">
                Created on{' '}
                {new Date(story.createdAt).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
              </p>
              <p className="text-[#6B4B3E]/75 mb-4" data-id="element-160">
                A story about {story.summary.name} with{' '}
                {story.summary.topics.join(', ')}
              </p>
              {story.apiResponse && story.apiResponse.book_id && <div className="mt-4 border-t border-purple-100 pt-4 space-y-4" data-id="element-161">
                  <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg" data-id="element-162">
                    Book ID: {story.apiResponse.book_id}
                  </div>
                  <a href={`https://storybunny.messagesimproved.com/?book_id=${story.apiResponse.book_id}#/storybook`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors w-full" data-id="element-163">
                    <BookOpenIcon size={20} data-id="element-164" />
                    <span data-id="element-165">Read Story</span>
                  </a>
                </div>}
            </div>)}
      </div>
    </div>;
};
const ChildAccounts = () => {
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [children, setChildren] = useState([{
    id: '1',
    name: 'Emma',
    avatar: '🦄',
    stories: 12,
    age: 7,
    readingLevel: 'intermediate' as const
  }]);
  const handleSaveChild = (childData: {
    name: string;
    avatar: string;
    age: number;
    readingLevel: 'beginner' | 'intermediate' | 'advanced';
  }) => {
    setChildren([...children, {
      id: Date.now().toString(),
      name: childData.name,
      avatar: childData.avatar,
      stories: 0,
      age: childData.age,
      readingLevel: childData.readingLevel
    }]);
    setShowCreateAccount(false);
  };
  return <div className="space-y-6" data-id="element-166">
      <div className="flex items-center justify-between" data-id="element-167">
        <h2 className="text-xl font-bold text-[#6B4B3E]" data-id="element-168">Child Accounts</h2>
        <button onClick={() => setShowCreateAccount(true)} className="flex items-center space-x-2 px-4 py-2 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-169">
          <PlusIcon size={20} data-id="element-170" />
          <span data-id="element-171">Add Child</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-172">
        {children.map(child => <div key={child.id} className="bg-white p-4 rounded-xl shadow-sm" data-id="element-173">
            <div className="flex items-center space-x-4" data-id="element-174">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-2xl" data-id="element-175">
                {child.avatar}
              </div>
              <div className="flex-1" data-id="element-176">
                <h3 className="font-bold text-[#6B4B3E]" data-id="element-177">{child.name}</h3>
                <div className="space-y-1" data-id="element-178">
                  <p className="text-sm text-[#6B4B3E]/75" data-id="element-179">
                    {child.stories} stories created
                  </p>
                  <div className="flex items-center space-x-2" data-id="element-180">
                    <span className="text-xs bg-[#FFF0E6] text-[#6B4B3E] px-2 py-1 rounded-full" data-id="element-181">
                      Age {child.age}
                    </span>
                    <span className="text-xs bg-[#FFF0E6] text-[#6B4B3E] px-2 py-1 rounded-full capitalize" data-id="element-182">
                      {child.readingLevel} Reader
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => handleChildSelect(child.id)} className="text-[#D4A088] hover:text-[#C28B73]" data-id="element-183">
                <ChevronRightIcon size={20} data-id="element-184" />
              </button>
            </div>
          </div>)}
      </div>
      {showCreateAccount && <CreateChildAccount onClose={() => setShowCreateAccount(false)} onSave={handleSaveChild} data-id="element-185" />}
    </div>;
};
const StoryRules = () => {
  const [likes, setLikes] = useState('bunnies, trains, kindness');
  const [restrictions, setRestrictions] = useState(['Scary content']);
  const [storyLength, setStoryLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [themes, setThemes] = useState(['Friendship', 'Adventure']);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const restrictionOptions = ['Scary content', 'Complex emotions', 'Bad language', 'Mean behavior', 'Dangerous activities', 'Sad endings'];
  return <div className="space-y-6" data-id="element-186">
      <h2 className="text-xl font-bold text-[#6B4B3E]" data-id="element-187">Story Rules</h2>
      <div className="bg-white p-6 rounded-xl shadow-sm space-y-6" data-id="element-188">
        <div data-id="element-189">
          <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-190">
            Things We Like
          </label>
          <textarea value={likes} onChange={e => setLikes(e.target.value)} className="w-full h-24 p-3 border border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] focus:ring-2 focus:ring-[#E5C1A9]/50 resize-none" data-id="element-191" />
        </div>
        <div data-id="element-192">
          <div className="flex items-center justify-between mb-4" data-id="element-193">
            <label className="text-[#6B4B3E] font-medium" data-id="element-194">No-No List</label>
            <button onClick={() => setShowDisclaimer(!showDisclaimer)} className="text-[#D4A088] hover:text-[#C28B73] flex items-center text-sm" data-id="element-195">
              <InfoIcon size={16} className="mr-1" data-id="element-196" />
              View Safety Disclaimer
            </button>
          </div>
          {showDisclaimer && <div className="mb-4 p-4 bg-[#FFF0E6] rounded-lg border border-[#E5C1A9]" data-id="element-197">
              <div className="flex items-start" data-id="element-198">
                <AlertCircleIcon size={20} className="text-[#D4A088] mt-1 mr-2 flex-shrink-0" data-id="element-199" />
                <div className="text-sm text-[#6B4B3E]" data-id="element-200">
                  <p className="font-medium mb-2" data-id="element-201">Safety First!</p>
                  <p className="mb-2" data-id="element-202">
                    In addition to your selected restrictions, we automatically
                    filter:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-203">
                    <li data-id="element-204">Inappropriate content for children</li>
                    <li data-id="element-205">Personal information and identifiers</li>
                    <li data-id="element-206">External links and contact information</li>
                    <li data-id="element-207">References to real people or current events</li>
                  </ul>
                </div>
              </div>
            </div>}
          <div className="grid grid-cols-2 gap-4" data-id="element-208">
            {restrictionOptions.map(option => <label key={option} className="flex items-center space-x-3" data-id="element-209">
                <input type="checkbox" checked={restrictions.includes(option)} onChange={e => {
              if (e.target.checked) {
                setRestrictions([...restrictions, option]);
              } else {
                setRestrictions(restrictions.filter(r => r !== option));
              }
            }} className="form-checkbox h-5 w-5 text-[#D4A088] rounded border-[#E5C1A9]" data-id="element-210" />
                <span className="text-[#6B4B3E]" data-id="element-211">{option}</span>
              </label>)}
          </div>
        </div>
        <div data-id="element-212">
          <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-213">
            Story Length
          </label>
          <div className="flex space-x-4" data-id="element-214">
            {['short', 'medium', 'long'].map(length => <button key={length} onClick={() => setStoryLength(length as typeof storyLength)} className={`flex-1 py-2 px-4 rounded-full border-2 transition-colors ${storyLength === length ? 'border-[#D4A088] bg-[#FFF0E6] text-[#6B4B3E]' : 'border-[#E5C1A9] text-[#6B4B3E]/75 hover:border-[#D4A088]'}`} data-id="element-215">
                {length.charAt(0).toUpperCase() + length.slice(1)}
              </button>)}
          </div>
        </div>
        <div className="flex justify-end" data-id="element-216">
          <button onClick={() => {}} className="px-6 py-2 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-217">
            Save Changes
          </button>
        </div>
      </div>
    </div>;
};